<template>
    <div class="p-6 pb-10 mb-15 shadow-md rounded-md">
        <h3 class="text-2xl font-bold">Logo, Intro & Email texts</h3>
        <p class="text-base leading-5">
            Default logo & texts
        </p>
        <div class="mt-6">
            <form @submit.prevent="submit">
                <div class="lg:w-1/2">
                    <div class="custom-field mb-6">
                        <div class="col-span-6 sm:col-span-4">
                            <!-- Profile Photo File Input -->
                            <input type="file" accept="image/*" class="hidden" ref="photo"
                                   @change="updateLogoPreview">
                            <label class="block text-sm mb-2">
                                <span class="text-lg font-semibold leading-6 mb-2.5">Logo</span>
                            </label><!-- Current Profile Photo -->
                            <div class="mt-2" v-if="!photoPreview && !form.surveyDefaults.logo">
                                <img
                                    src="/images/logo-placeholder.jpg"
                                    alt="Placeholder logo"
                                    class="h-20 max-w-md">
                            </div>
                            <div class="mt-2" v-else-if="!photoPreview && form.surveyDefaults.logo">
                                <img
                                    :src="form.surveyDefaults.logo_url"
                                    alt="Placeholder logo"
                                    class="h-20 max-w-md">
                            </div>
                            <!-- New Profile Photo Preview -->
                            <div class="mt-2" v-else>
                                <img
                                    :src="photoPreview"
                                    alt="Placeholder logo"
                                    class="h-20 max-w-md">
                            </div>
                            <jet-button type="button" class="mt-2"
                                        @click.prevent="selectNewLogo"
                                        :has-icon="true" :bg-dark="true"
                            >
                                Add your organisations logo
                            </jet-button>
                            <jet-button type="button" class="ml-2"
                                        @click.prevent="deleteLogo"
                                        :has-icon="true" :bg-dark="true"
                                                  v-if="form.surveyDefaults.logo">
                                Remove Logo
                            </jet-button>
                            <jet-input-error :message="form.errors['defaults.logo']"/>
                        </div>
                    </div>
                    <div class="custom-field mb-6">
                        <label class="block text-sm mb-2">
                            <span class="text-lg font-semibold leading-6 mb-2.5">Intro Text</span>
                        </label>
                        <ckeditor :editor="editor"
                                  v-model="form.surveyDefaults.intro_text"
                                  class="block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-textarea focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                                  :config="editorConfig"></ckeditor>
                        <jet-input-error :message="form.errors['defaults.intro_text']"/>
                    </div>
                    <div class="custom-field mb-6">
                        <label class="block text-sm mb-2">
                            <span class="text-lg font-semibold leading-6 mb-2.5">Email Text</span>
                        </label>
                        <ckeditor :editor="editor"
                                  v-model="form.surveyDefaults.email_text"
                                  class="block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-textarea focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray"
                                  :config="editorConfig"></ckeditor>
                        <jet-input-error :message="form.errors['defaults.email_text']"/>
                    </div>
                </div>
                <div class="flex justify-between items-center">
                    <jet-action-message :on="form.recentlySuccessful" class="ml-auto mr-3">
                        Saved.
                    </jet-action-message>
                    <jet-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" :bg-dark="true">
                        Save fields
                    </jet-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import JetTextarea from "@/Jetstream/Textarea";
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetInputError from "@/Jetstream/InputError";
import JetActionMessage from "@/Jetstream/ActionMessage";
import JetButton from '@/Jetstream/Button.vue'
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue";

export default {
    name: "SurveyDefaultsForm",
    components: {
        JetTextarea,
        JetSecondaryButton,
        JetInputError,
        JetActionMessage,
        JetButton,
        ckeditor: CKEditor.component
    },
    props: {
        surveyType: {
            type: Number,
            required: true,
        },
        defaults: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            form: this.$inertia.form({
                surveyType: this.surveyType,
                surveyDefaults: {
                    ...this.defaults,
                    email_text: this.defaults.email_text ?? '',
                    intro_text: this.defaults.intro_text ?? '',
                },
            }),
            photoPreview: null,
            editor: ClassicEditor,
            editorConfig: {
                removePlugins: ['imageUpload', 'mediaEmbed'],
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'http://',
                },
                toolbar: {
                    removeItems: ['uploadImage', 'mediaEmbed']
                },
            },
        }
    },
    methods: {
        updateLogoPreview() {
            const photo = this.$refs.photo.files[0];

            if (!photo) return;

            const reader = new FileReader();

            reader.onload = (e) => {
                this.photoPreview = e.target.result;
            };

            reader.readAsDataURL(photo);
        },
        selectNewLogo() {
            this.$refs.photo.click();
        },
        submit() {
            if (this.$refs.photo) {
                this.form.surveyDefaults.logo = this.$refs.photo.files[0];
            }

            this.form.transform((data) => ({
                surveyType: data.surveyType,
                defaults: data.surveyDefaults,
            })).put(route('admin.survey.defaults.save'), {
                errorBag: 'defaults',
                preserveScroll: true,
                onSuccess: () => {
                    this.photoPreview = null;
                    this.clearPhotoFileInput();
                    this.reinitializeSurveyDefaults();
                },
            });
        },
        clearPhotoFileInput() {
            if (this.$refs.photo?.value) {
                this.$refs.photo.value = null;
            }
        },
        deleteLogo() {
            this.$inertia.delete(route('admin.survey.defaults.remove-logo', {type: this.surveyType}), {
                preserveScroll: true,
                onSuccess: () => {
                    this.photoPreview = null;
                    this.clearPhotoFileInput();
                    this.reinitializeSurveyDefaults();
                },
            });
        },
        reinitializeSurveyDefaults() {
            this.form.surveyDefaults = {
                ...this.defaults,
                email_text: this.defaults.email_text ?? '',
                intro_text: this.defaults.intro_text ?? '',
            };
        }
    }
}
</script>

<style lang="scss">
@import "resources/sass/ck-editor-dark";
@import "resources/sass/ck-content";
</style>
